var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useRef, useState } from "react";
export function getUnderlyingPlayer(container) {
    var _a, _b;
    if (!container)
        return undefined;
    var root = container.querySelector(".video-js");
    var video = container.querySelector("video");
    return (_b = (_a = root === null || root === void 0 ? void 0 : root.player) !== null && _a !== void 0 ? _a : video === null || video === void 0 ? void 0 : video.player) !== null && _b !== void 0 ? _b : undefined;
}
var EMPTY_SESSION = {
    uptimeSeconds: 0,
    rebufferCount: 0,
    rebufferSeconds: 0,
    abrSwitches: 0,
    abrDownSwitches: 0
};
/**
 * Frame counters, whichever spelling this browser has.
 *
 * `getVideoPlaybackQuality()` is the standard one, and where it is missing (or
 * returns nothing for a stream the OS is decoding itself) WebKit's own counters
 * still answer, so Safari reports a dropped-frame ratio instead of "n/a".
 */
function readFrameCounters(videoEl) {
    var _a, _b, _c;
    var quality = (_a = videoEl === null || videoEl === void 0 ? void 0 : videoEl.getVideoPlaybackQuality) === null || _a === void 0 ? void 0 : _a.call(videoEl);
    return {
        totalFrames: (_b = quality === null || quality === void 0 ? void 0 : quality.totalVideoFrames) !== null && _b !== void 0 ? _b : videoEl === null || videoEl === void 0 ? void 0 : videoEl.webkitDecodedFrameCount,
        droppedFrames: (_c = quality === null || quality === void 0 ? void 0 : quality.droppedVideoFrames) !== null && _c !== void 0 ? _c : videoEl === null || videoEl === void 0 ? void 0 : videoEl.webkitDroppedFrameCount
    };
}
/** Reads what the public API does not carry: rendered size, frame counters, codecs. */
function sampleMediaElement(vjs, previousTotalFrames) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var tech = vjs.tech({ IWillNotUseThisInPlugins: true });
    var videoEl = (_a = tech === null || tech === void 0 ? void 0 : tech.el) === null || _a === void 0 ? void 0 : _a.call(tech);
    var _j = readFrameCounters(videoEl), totalFrames = _j.totalFrames, droppedFrames = _j.droppedFrames;
    return {
        width: (_b = vjs.videoWidth()) !== null && _b !== void 0 ? _b : 0,
        height: (_c = vjs.videoHeight()) !== null && _c !== void 0 ? _c : 0,
        fps: previousTotalFrames !== undefined && totalFrames !== undefined ? Math.max(0, totalFrames - previousTotalFrames) : undefined,
        droppedFrames: droppedFrames,
        totalFrames: totalFrames,
        // Straight off the manifest, so only ever available on the engine path.
        codecs: (_h = (_g = (_f = (_e = (_d = tech === null || tech === void 0 ? void 0 : tech.vhs) === null || _d === void 0 ? void 0 : _d.playlists) === null || _e === void 0 ? void 0 : _e.media) === null || _f === void 0 ? void 0 : _f.call(_e)) === null || _g === void 0 ? void 0 : _g.attributes) === null || _h === void 0 ? void 0 : _h.CODECS
    };
}
/**
 * Samples the player once a second for as long as `enabled` is true.
 *
 * @param containerRef Element wrapping the player, used to find the video.js root.
 * @param player       The player instance, once it exists.
 * @param enabled      Set false to keep the panel (and its timer) switched off.
 */
export var useStreamDiagnostics = function (containerRef, player, enabled) {
    var _a = useState(null), diagnostics = _a[0], setDiagnostics = _a[1];
    var _b = useState(EMPTY_SESSION), session = _b[0], setSession = _b[1];
    // Counters the 1s tick and the player listeners share. Refs rather than
    // state so neither has to re-subscribe when the other updates.
    var previousTotalFrames = useRef(undefined);
    var startedAt = useRef(undefined);
    var rebuffer = useRef({ count: 0, seconds: 0 });
    var abr = useRef({ switches: 0, downSwitches: 0 });
    useEffect(function () {
        if (!enabled || !player)
            return;
        // `stall` is the player's own rebuffer report, and it already excludes the
        // buffering that precedes the first frame (that is startup, not a stall).
        var onStall = function (_a) {
            var duration = _a.duration;
            rebuffer.current = { count: rebuffer.current.count + 1, seconds: rebuffer.current.seconds + duration };
        };
        // `qualitychange` covers deliberate picks only; `renditionchange` is what
        // reports the adaptive selector's own switches, and carries the level it
        // came from so a step down is visible.
        var onRenditionChange = function (_a) {
            var level = _a.level, previous = _a.previous;
            abr.current = {
                switches: abr.current.switches + 1,
                downSwitches: abr.current.downSwitches + (previous && level.bitrate < previous.bitrate ? 1 : 0)
            };
        };
        player.on("stall", onStall);
        player.on("renditionchange", onRenditionChange);
        var timer = window.setInterval(function () {
            var vjs = getUnderlyingPlayer(containerRef.current);
            if (!vjs)
                return;
            var media = sampleMediaElement(vjs, previousTotalFrames.current);
            previousTotalFrames.current = media.totalFrames;
            // The clock starts with playback rather than with the panel, so "on air
            // for" reads as time on screen and not time since the page loaded.
            if (startedAt.current === undefined && !player.paused)
                startedAt.current = Date.now();
            setDiagnostics(__assign(__assign({}, media), { stats: player.getPlaybackStats() }));
            setSession({
                uptimeSeconds: startedAt.current !== undefined ? (Date.now() - startedAt.current) / 1000 : 0,
                rebufferCount: rebuffer.current.count,
                rebufferSeconds: rebuffer.current.seconds,
                abrSwitches: abr.current.switches,
                abrDownSwitches: abr.current.downSwitches
            });
        }, 1000);
        return function () {
            window.clearInterval(timer);
            player.off("stall", onStall);
            player.off("renditionchange", onRenditionChange);
        };
    }, [containerRef, player, enabled]);
    return { diagnostics: diagnostics, session: session };
};
